import React, { PureComponent } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { reduxForm, SubmissionError } from 'redux-form';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';

import { banner } from 'actions';
import { Button, Input } from 'common/components';
import { graphQlToFormErrors } from 'common/utils';
import { required, numberOnly, notOneOf } from 'common/form-validators';
import { trim } from 'common/normalizers';
import { BANNER_IDS, ERROR_DEFAULT } from 'common/constants';
import {
  MUTATION_CREATE_FERTILIZER,
  FETCH_ALL_FERTILIZERS,
} from 'api/activity';
// import mixpanel from 'mixpanel-browser';

export class FertilizerForm extends PureComponent {
  // fertilizer name field validation
  nameTaken = value => notOneOf(value, this.props.existingFertilizerNames);

  render() {
    const {
      onSubmit,
      closeForm,
      error,
      handleSubmit,
      valid,
      pristine,
      submitting,
      selectedFertilizer,
      activityFertilizer,
    } = this.props;

    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <h2 className={'text--header'}>
          {selectedFertilizer || activityFertilizer ? 'EDIT' : 'ADD'} fertilizer
        </h2>

        <small className={'text--info'}>* Required</small>

        <Input
          name="name"
          disabled={submitting}
          title="Fertilizer Name"
          validate={[required, this.nameTaken]}
          normalizeOnBlur={trim}
        />

        <div className={'columns columns__row'}>
          <div className={'column is-half is-tight'}>
            <Input
              name="nitrogen"
              disabled={submitting}
              title="Nitrogen (N)%"
              validate={[numberOnly]}
              theme={['tight']}
            />
          </div>
          <div className={'column is-half is-tight'}>
            <Input
              name="phosphorus"
              disabled={submitting}
              title="Phosphorus (P)%"
              validate={[numberOnly]}
              theme={['tight']}
            />
          </div>
        </div>

        <div className={'columns columns__row'}>
          <div className={'column is-half is-tight'}>
            <Input
              name="potassium"
              disabled={submitting}
              title="Potassium (K)%"
              validate={[numberOnly]}
              theme={['tight']}
            />
          </div>
          <div className={'column is-half is-tight'}>
            <Input
              name="sulfur"
              disabled={submitting}
              title="Sulfur (S)%"
              validate={[numberOnly]}
              theme={['tight']}
            />
          </div>
        </div>

        <div className={'columns columns__row'}>
          <div className={'column is-half is-tight'}>
            <Input
              name="zinc"
              disabled={submitting}
              title="Zinc (Z)%"
              validate={[numberOnly]}
              theme={['tight']}
            />
          </div>
        </div>

        {error && <p className="text--error">{error}</p>}

        <div className={'field__group'}>
          <fieldset className={'field text--center'}>
            <Button
              type="submit"
              className="btn btn--primary gap-top-small"
              disabled={!valid || pristine || submitting}
            >
              Save
            </Button>
          </fieldset>

          <fieldset className={'field text--center'}>
            <Button
              type="cancel"
              className="btn"
              onClick={closeForm}
              disabled={submitting}
            >
              Cancel
            </Button>
          </fieldset>
        </div>
      </form>
    );
  }
}

FertilizerForm.propTypes = {
  // redux form
  error: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  valid: PropTypes.bool.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  // map dispatch
  onSubmit: PropTypes.func.isRequired,

  // manually added
  closeForm: PropTypes.func.isRequired,
  changeValue: PropTypes.func,
  selectedFertilizer: PropTypes.object, // for potential edit fertilizer
  activityFertilizer: PropTypes.object, // for activity

  // from graphQL
  loading: PropTypes.bool,
  existingFertilizerNames: PropTypes.array,
};

const formSettings = { form: 'fertilizerform', enableReinitialize: true };
const mapState = (state, { ownProps }) => {
  let props = {};
  if (ownProps && ownProps.activityFertilizer) {
    const {
      nitrogen,
      phosphorus,
      potassium,
      sulfur,
      zinc,
    } = ownProps.activityFertilizer;
    props = {
      ...props,
      initialValues: {
        ...ownProps.activityFertilizer,
        // because redux-form doesn't prepopulate number 0, but does for string 0
        nitrogen: nitrogen || '0',
        phosphorus: phosphorus || '0',
        potassium: potassium || '0',
        sulfur: sulfur || '0',
        zinc: zinc || '0',
      },
    };
  }
  return props;
};

const mapDispatch = (dispatch, props) => ({
  onSubmit: async values => {
    const {
      name,
      nitrogen = 0,
      phosphorus = 0,
      potassium = 0,
      sulfur = 0,
      zinc = 0,
    } = values;
    let vars = { name, nitrogen, phosphorus, potassium, sulfur, zinc };

    // just for activity form
    if (props.activityFertilizer) {
      props.closeForm();
      if (props.changeValue) {
        props.changeValue(
          props.activityFertilizer.fieldname,
          JSON.stringify(vars)
        );
      }

      // for creating / editing actual fertilizers
    } else {
      const res = await props.onCreateFertilizer(vars);

      if (res && res.data && res.data.createFertilizer) {
        props.closeForm(res.data.createFertilizer);
        dispatch(
          banner.display(
            BANNER_IDS.APP,
            `FERTILIZER ${props.id ? 'UPDATED' : 'ADDED'}`,
            false
          )
        );
      } else if (res && res.errors) {
        const _error = res.errors[0] ? res.errors[0].message : ERROR_DEFAULT;
        throw new SubmissionError({ _error });
      } else {
        throw new SubmissionError({ _error: ERROR_DEFAULT });
      }
    }
  },
});

export default compose(
  // for fertilizer name validation
  graphql(FETCH_ALL_FERTILIZERS, {
    options: () => ({
      fetchPolicy: 'cache-and-network',
    }),
    props: props => {
      const {
        data: { getAllFertilizers },
        ownProps: { activityFertilizer },
      } = props;
      let existingFertilizerNames = [];
      if (getAllFertilizers && getAllFertilizers.length) {
        existingFertilizerNames = getAllFertilizers.map(
          fertilizer => fertilizer.name
        );
        if (activityFertilizer) {
          existingFertilizerNames = existingFertilizerNames.filter(
            fertilizer => fertilizer !== activityFertilizer.name
          );
        }
      }
      return { existingFertilizerNames, ...props };
    },
  }),

  graphql(MUTATION_CREATE_FERTILIZER, {
    props: props => ({
      onCreateFertilizer: fertilizerCreateInput => {
        return props
          .mutate({
            variables: { fertilizerCreateInput },
            optimisticResponse: {
              createFertilizer: {
                __typename: 'Fertilizer',
                id: `FERTILIZER:${fertilizerCreateInput.name}`,
                ...fertilizerCreateInput,
              },
            },
            update: proxy => {
              // Read the data from our cache for this query.
              const proxyData = proxy.readQuery({
                query: FETCH_ALL_FERTILIZERS,
              });
              const updatedData = {
                __typename: 'Fertilizer',
                id: `FERTILIZER:${fertilizerCreateInput.name}`,
                ...fertilizerCreateInput,
              };

              proxyData.getAllFertilizers.push(updatedData);

              // Add our comment from the mutation to the end.
              // Write our data back to the cache.
              proxy.writeQuery({
                query: FETCH_ALL_FERTILIZERS,
                data: proxyData,
              });
            },
          })
          .then(res => res)
          .catch(graphQlToFormErrors);
      },
      ...props,
    }),
  }),

  connect(
    mapState,
    mapDispatch
  ),
  reduxForm(formSettings)
)(FertilizerForm);



// WEBPACK FOOTER //
// ./src/containers/FertilizerForm/index.js